import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, Star, Zap, Users, Award, Laptop, Bot, Video, Smartphone, AlertTriangle, Shield, ChevronDown, ChevronUp, Facebook, Instagram, Youtube, Linkedin, MessageCircleDashed as MessageCircle, DollarSign, X, Frown, BookOpen, Target, Trophy, CheckCircle, Volume2, RotateCcw, TrendingUp, Globe, Briefcase, BookOpenCheck, TrendingDown } from 'lucide-react';

const ValeBonus: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const [availableSpots, setAvailableSpots] = useState(576);
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  useEffect(() => {
    // Google Tag Manager
    const gtmScript = document.createElement('script');
    gtmScript.async = true;
    gtmScript.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-PR5KTQF8';
    document.head.appendChild(gtmScript);

    const gtmDataLayer = document.createElement('script');
    gtmDataLayer.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'GTM-PR5KTQF8');
    `;
    document.head.appendChild(gtmDataLayer);

    // GTM noscript fallback
    const noscript = document.createElement('noscript');
    noscript.innerHTML = '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-PR5KTQF8" height="0" width="0" style="display:none;visibility:hidden"></iframe>';
    document.body.insertBefore(noscript, document.body.firstChild);

    const targetDate = new Date();
    targetDate.setDate(targetDate.getDate() + 14); // 14 days from now

    const timer = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60));
      const minutes = Math.floor(distance % (1000 * 60 * 60) / (1000 * 60));
      const seconds = Math.floor(distance % (1000 * 60) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });

      if (distance < 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => {
      clearInterval(timer);
      // Cleanup scripts
      const scripts = document.querySelectorAll('script[src*="converteai.net"], script[src*="googletagmanager.com"]');
      scripts.forEach((script) => script.remove());
    };
  }, []);

  // Simular redução de vagas
  useEffect(() => {
    const spotsTimer = setInterval(() => {
      setAvailableSpots((prev) => {
        if (prev > 36) {
          return prev - Math.floor(Math.random() * 3) - 1;
        }
        return prev;
      });
    }, 30000);

    return () => clearInterval(spotsTimer);
  }, []);

  const benefits = [
  { icon: Laptop, text: "EW Academy: mais de 400 horas de aulas" },
  { icon: Bot, text: "Lucy IA - Tutora virtual 24h, no seu whatsapp" },
  { icon: Video, text: "EW Live: Aulas ao vivo semanais" },
  { icon: Smartphone, text: "Apps iOS e Android para estudar onde quiser. Até offline" },
  { icon: Users, text: "Comunidade exclusiva com gamificação" },
  { icon: Award, text: "Certificado incluso de cada curso finalizado" }];

  const testimonials = [
  {
    name: "Marina Silva",
    photo: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop&crop=face",
    text: "Em 12 meses consegui minha primeira promoção internacional! O EW Pass mudou minha carreira completamente.",
    role: "Gerente de Marketing"
  },
  {
    name: "Carlos Mendes",
    photo: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop&crop=face",
    text: "Finalmente consegui assistir filmes sem legenda e me comunicar com confiança em reuniões internacionais.",
    role: "Engenheiro de Software"
  },
  {
    name: "Ana Paula",
    photo: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop&crop=face",
    text: "A Lucy IA é incrível! Sempre disponível para tirar dúvidas. Nunca pensei que aprender inglês pudesse ser tão fácil.",
    role: "Estudante de Medicina"
  },
  {
    name: "Roberto Santos",
    photo: "https://images.pexels.com/photos/2182970/pexels-photo-2182970.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop&crop=face",
    text: "Consegui uma vaga nos EUA depois de 1 ano estudando. O método funciona mesmo, não é só marketing!",
    role: "Analista Financeiro"
  }];

  const problems = [
  {
    icon: DollarSign,
    title: "Cursos Caros",
    description: "Escolas tradicionais cobram milhares de reais por ano"
  },
  {
    icon: Clock,
    title: "Falta de Tempo",
    description: "Horários fixos que não se adaptam à sua rotina"
  },
  {
    icon: X,
    title: "Métodos Ultrapassados",
    description: "Livros e aulas monótonas que não funcionam"
  },
  {
    icon: Frown,
    title: "Alta Desistência",
    description: "90% dos alunos desistem nos primeiros 6 meses"
  }];

  const journeySteps = [
  {
    icon: BookOpen,
    title: "Método GO 4D",
    description: "Desde a primeira aula você já começa a prática da compreensão e conversação do inglês, com tutora virtual, aulas ao vivo semanais e mais de 400 horas de conteúdo exclusivo",
    gradient: "from-blue-500 via-blue-600 to-purple-600",
    number: "01"
  },
  {
    icon: Target,
    title: "Prática constante com a Lucy IA",
    description: "Direto no seu Whatsapp, 24 horas por dia, a tutora Lucy IA está ao seu lado te orientando e ajudando na prática, sem ter medo e vergonha de treinar o seu inglês",
    gradient: "from-green-500 via-green-600 to-emerald-600",
    number: "02"
  },
  {
    icon: Users,
    title: "EW Academy",
    description: "Não apenas uma IA, contamos com mais de 400 horas de aulas, do básico ao avançado, te mostrando o melhor caminho para sua jornada de aprendizagem.",
    gradient: "from-purple-500 via-purple-600 to-pink-600",
    number: "03"
  },
  {
    icon: Trophy,
    title: "Fluência Conquistada",
    description: "Você falando inglês com confiança em situações reais do dia a dia. Porque falar inglês não é só sobre aprender palavras, é sobre abrir portas, conquistar oportunidades e se conectar com o mundo.",
    gradient: "from-yellow-500 via-orange-500 to-red-500",
    number: "04"
  }];

  const guaranteeFeatures = [
  {
    icon: Clock,
    title: "14 Dias",
    description: "Tempo total para testar nossa plataforma sem riscos",
    color: "blue"
  },
  {
    icon: CheckCircle,
    title: "100% do Dinheiro",
    description: "Devolução integral sem perguntas ou burocracias",
    color: "green"
  },
  {
    icon: Shield,
    title: "Sem Riscos",
    description: "Você não tem nada a perder, apenas fluência a ganhar",
    color: "purple"
  }];

  const guaranteeSteps = [
  {
    number: 1,
    title: "Assine a EW Pass Pro",
    description: "Faça sua assinatura e tenha acesso completo"
  },
  {
    number: 2,
    title: "Teste por 14 dias completos",
    description: "Explore todo o conteúdo e metodologia"
  },
  {
    number: 3,
    title: "Se não gostar, devolvemos 100%",
    description: "Sem perguntas, sem burocracias"
  }];

  const solutionFeatures = [
  {
    icon: Volume2,
    title: "Método GO 4D",
    description: "Nossa metodologia exclusiva que acelera seu aprendizado através de 4 dimensões: Ouvir, falar, ler e escrever. Treino, pronúncia e compreensão diárias usando a Lucy IA"
  },
  {
    icon: Clock,
    title: "15 minutos por dia",
    description: "Apenas 15 minutos diários são suficientes para você dominar o inglês de forma consistente e eficaz."
  },
  {
    icon: RotateCcw,
    title: "Foco na conversação",
    description: "Priorizamos a prática oral desde o primeiro dia, para que você ganhe confiança para falar inglês."
  },
  {
    icon: TrendingUp,
    title: "Progresso acelerado",
    description: "Veja resultados reais em semanas, não anos. Nossa metodologia é comprovadamente mais rápida."
  }];

  const englishBenefits = [
  {
    icon: Briefcase,
    title: "Mais oportunidades profissionais",
    description: "Dominar o inglês abre portas para novas oportunidades de trabalho no Brasil e no mundo, presencial ou home office.",
    image: "https://images.pexels.com/photos/7688336/pexels-photo-7688336.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop"
  },
  {
    icon: BookOpenCheck,
    title: "Acesso a conteúdos em primeira mão",
    description: "Grande parte dos conteúdos é gerada na língua inglesa. Saia na frente dos seus concorrentes.",
    image: "https://images.pexels.com/photos/159711/books-bookstore-book-reading-159711.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop"
  },
  {
    icon: Globe,
    title: "Comunicação Global",
    description: "O mundo se abre para quem fala inglês. Em quase todos os países do globo você conseguirá conversar em inglês.",
    image: "https://images.pexels.com/photos/1595385/pexels-photo-1595385.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop"
  },
  {
    icon: TrendingUp,
    title: "Melhores Salários",
    description: "O profissional que fala inglês tem um salário em média 61% maior do que quem não fala o idioma e é a maior causa de progressão na carreira para 50% dos empregadores.",
    image: "https://images.pexels.com/photos/164527/pexels-photo-164527.jpeg?auto=compress&cs=tinysrgb&w=400&h=300&fit=crop"
  }];

  const faqData = [
  {
    question: "Como funciona o acesso vitalício?",
    answer: "Com o acesso vitalício, você paga uma única vez e tem acesso para sempre a todo o conteúdo do EW Pass, incluindo atualizações futuras. Não há mensalidades ou taxas adicionais."
  },
  {
    question: "Posso acessar o conteúdo offline?",
    answer: "Sim! Através dos nossos apps para iOS e Android, você pode baixar as aulas e estudar offline, onde e quando quiser, sem depender de conexão com a internet."
  },
  {
    question: "A Lucy IA realmente funciona 24 horas?",
    answer: "Sim! A Lucy é nossa tutora virtual que está disponível 24/7 para tirar suas dúvidas, corrigir exercícios e te ajudar em qualquer momento do seu aprendizado."
  },
  {
    question: "Qual é o nível do curso? É para iniciantes?",
    answer: "O EW Pass atende todos os níveis, do básico ao avançado. Temos conteúdo estruturado que se adapta ao seu nível atual e te leva até a fluência completa."
  },
  {
    question: "Como funciona a garantia de 14 dias?",
    answer: "Se por qualquer motivo você não ficar satisfeito com o EW Pass nos primeiros 14 dias, devolvemos 100% do seu dinheiro, sem perguntas ou burocracias."
  },
  {
    question: "Recebo certificado ao final do curso?",
    answer: "Sim! Você recebe um certificado de conclusão reconhecido que comprova suas habilidades em inglês, válido para usar no seu currículo e LinkedIn."
  },
  {
    question: "As aulas ao vivo são obrigatórias?",
    answer: "Não! As aulas ao vivo são um bônus extra. Todo o conteúdo principal está disponível gravado para você assistir no seu ritmo. As lives são para tirar dúvidas e praticar."
  },
  {
    question: "Posso parcelar o pagamento?",
    answer: "Sim! Você pode pagar à vista por R$ 297 ou parcelar em até 10x de R$ 33,44 no cartão de crédito, sem juros adicionais."
  }];

  // Função para rolar até a oferta
  const scrollToOffer = () => {
    const offerSection = document.getElementById('oferta-final');
    if (offerSection) {
      offerSection.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative py-6 px-4 z-20">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://static.lumi.new/65/65f85551bb8e2acf68da5047ed864ca5.webp"
              alt="EW PASS Logo"
              className="h-10 md:h-12 w-auto" />
            {/* Logo de parceria ValeBonus no header */}
            <div className="flex items-center gap-2">
              <span className="text-yellow-400 text-xs font-bold">Parceria</span>
              <img
                src="https://cdn-static-lumi.artvibe.ai/05/059de236d95421a6dea4ecd5fcb8420b.png"
                alt="ValeBonus"
                className="h-10 md:h-12 w-auto" />
            </div>
          </div>
          <div className="text-right">
            



          </div>
        </div>
      </motion.header>

      {/* Main Headline */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="pt-[48px] pl-[16px] pr-[16px] pb-[6px]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-3xl md:text-5xl font-bold text-yellow-400 mb-4">🔥 INVISTA EM VOCÊ HOJE 🔥</div>
          <h2 className="text-4xl md:text-6xl font-bold mb-8 leading-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">Nunca mais gaste nenhum centavo</span> para aprender inglês
          </h2>
        </div>
      </motion.section>

      {/* Texto descritivo */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
        className="pt-[12px] pl-[16px] pr-[16px] pb-[32px]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="space-y-6 text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
            <p>
              Domine o inglês, conquiste fluência e multiplique suas oportunidades. 
              <span className="text-yellow-400 font-semibold"> ACESSO VITALÍCIO</span> a todo conteúdo do EW Pass.
            </p>
            <p>
              O maior desconto que já fizemos e <span className="text-green-400 font-semibold">não vai se repetir tão cedo! </span>
            </p>
            <p className="text-yellow-300 font-semibold">E essa é uma condição exclusiva!</p>
            <p>
              <span className="text-green-400 font-semibold">Compre UMA VEZ, estude para SEMPRE!</span>
            </p>
          </div>
        </div>
      </motion.section>

      {/* Urgência de Vagas Limitadas */}
      <motion.section
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.6 }}
        className="py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-2xl p-8 text-center">
            <div className="flex items-center justify-center gap-2 mb-6">
              <AlertTriangle className="w-8 h-8 text-yellow-400" />
              <span className="text-yellow-400 font-bold text-xl">VAGAS LIMITADAS</span>
            </div>
            
            <div className="mb-6">
              <div className="text-6xl md:text-7xl font-bold text-white mb-2">
                {availableSpots.toLocaleString()}
              </div>
              <div className="text-lg text-yellow-300 font-semibold mb-4">
                vagas restantes
              </div>
              <div className="text-gray-200 text-base">
                Apenas <span className="text-yellow-400 font-bold">1500 pessoas</span> terão acesso a essa promoção exclusiva
              </div>
            </div>

            {/* Barra de Progresso das Vagas */}
            <div className="bg-black/30 rounded-full h-4 mb-4 overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: `${(1500 - availableSpots) / 1500 * 100}%` }}
                transition={{ duration: 1, ease: "easeOut" }} />
            </div>
            
            <div className="text-sm text-gray-200">
              <span className="text-yellow-400 font-semibold">{1500 - availableSpots}</span> pessoas já garantiram sua vaga
            </div>
          </div>
        </div>
      </motion.section>

      {/* Benefits Grid */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Tudo que você recebe <span className="text-yellow-400">PARA SEMPRE</span>
          </h2>
          
          {/* Banner Principal - Logo após o título */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mb-12">
            <div className="text-center">
              <img
                src="https://static.lumi.new/9a/9a1d1f1975e8af201d3e4aea5f728987.webp"
                alt="Banner Principal EW Pass"
                className="w-full max-w-4xl mx-auto rounded-2xl shadow-2xl" />
            </div>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-gray-900 border border-gray-700 rounded-xl p-6 hover:border-yellow-400 transition-all duration-300">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <p className="text-xl font-semibold">{benefit.text}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </motion.section>

      {/* Testimonials Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="pt-[32px] pl-[16px] pr-[16px] pb-[32px]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Star className="w-8 h-8 text-yellow-400 fill-current" />
              <Star className="w-8 h-8 text-yellow-400 fill-current" />
              <Star className="w-8 h-8 text-yellow-400 fill-current" />
              <Star className="w-8 h-8 text-yellow-400 fill-current" />
              <Star className="w-8 h-8 text-yellow-400 fill-current" />
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-4">
              O que nossos <span className="text-yellow-400">alunos dizem</span>
            </h3>
            <p className="text-gray-300 text-lg">
              Histórias reais de transformação e sucesso
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-gray-900 border border-gray-700 rounded-xl p-6 hover:border-yellow-400 transition-all duration-300">
                
                <div className="flex items-center gap-4 mb-4">
                  <img
                  src={testimonial.photo}
                  alt={testimonial.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-yellow-400" />

                  <div>
                    <h4 className="font-semibold text-white">{testimonial.name}</h4>
                    <p className="text-sm text-gray-400">{testimonial.role}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) =>
                <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                )}
                </div>
                
                <p className="text-gray-300 italic">"{testimonial.text}"</p>
              </motion.div>
            )}
          </div>

          <div className="text-center mt-12">
            <div className="bg-gradient-to-r from-yellow-400/20 to-orange-500/20 rounded-2xl p-8 border border-yellow-400/30">
              <p className="text-3xl md:text-4xl font-bold text-yellow-400 mb-2">
                Mais de <span className="text-5xl md:text-6xl">5.000 alunos</span>
              </p>
              <p className="text-xl md:text-2xl text-white font-semibold">
                já transformaram suas vidas com o EW Pass
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* English Importance Disclaimer Section */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-black pt-[32px] pl-[16px] pr-[16px] pb-[32px]">
        <div className="max-w-6xl mx-auto">
          {/* Título Principal */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-yellow-400 mb-8 leading-tight">
              Por que o inglês tem que ser pra todo mundo?
            </h2>
          </motion.div>

          {/* Grid de Estatísticas */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Card Salário */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-gray-900 via-black to-gray-900 border border-gray-700 rounded-3xl p-8 hover:border-yellow-400/50 transition-all duration-300">
              
              {/* Ícone e Título */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                  <DollarSign className="w-6 h-6 text-black font-bold" />
                </div>
                <h3 className="text-2xl font-bold text-yellow-400">Salário</h3>
              </div>
              
              {/* Conteúdo */}
              <div className="space-y-4">
                <p className="text-lg text-white leading-relaxed">
                  Quem fala inglês no Brasil ganha em média <span className="text-yellow-400 font-bold">61% a mais.</span>
                </p>
                <p className="text-sm text-gray-400">
                  (British Council)
                </p>
              </div>
            </motion.div>

            {/* Card Carreira */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-gray-900 via-black to-gray-900 border border-gray-700 rounded-3xl p-8 hover:border-yellow-400/50 transition-all duration-300">
              
              {/* Ícone e Título */}
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                  <TrendingUp className="w-6 h-6 text-black" />
                </div>
                <h3 className="text-2xl font-bold text-yellow-400">Carreira</h3>
              </div>
              
              {/* Conteúdo */}
              <div className="space-y-4">
                <p className="text-lg text-white leading-relaxed">
                  O inglês é a maior causa de progressão na carreira para <span className="text-yellow-400 font-bold">50% dos empregadores.</span>
                </p>
                <p className="text-sm text-gray-400">
                  (Cambridge)
                </p>
              </div>
            </motion.div>
          </div>

          {/* Linha divisória decorativa */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="w-full h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent rounded-full mt-16">
          </motion.div>
        </div>
      </motion.section>

      {/* Journey Section - Redesigned */}
      <motion.section
        id="rumo-a-fluencia"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-[32px] pl-[16px] pr-[16px] pb-[32px]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400/20 to-orange-500/20 rounded-full px-6 py-2 border border-yellow-400/30 mb-6">
              <Zap className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-400 font-semibold">MÉTODO EXCLUSIVO</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Sua Jornada Rumo à <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">Fluência</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Descubra como o <span className="text-yellow-400 font-semibold">Método GO 4D</span> vai transformar seu inglês em apenas algumas semanas
            </p>
          </motion.div>

          {/* Journey Steps - New Layout */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            {journeySteps.map((step, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="group relative">
                
                {/* Card */}
                <div className="relative bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-3xl p-8 hover:border-yellow-400/50 transition-all duration-500 hover:transform hover:scale-105 hover:shadow-2xl hover:shadow-yellow-400/20">
                  
                  {/* Number Badge */}
                  <div className="absolute -top-4 -left-4 z-10">
                    <div className={`w-16 h-16 bg-gradient-to-r ${step.gradient} rounded-2xl flex items-center justify-center shadow-lg transform rotate-12 group-hover:rotate-0 transition-transform duration-500`}>
                      <span className="text-white font-bold text-xl">{step.number}</span>
                    </div>
                  </div>

                  {/* Glow Effect */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${step.gradient} rounded-3xl opacity-0 group-hover:opacity-10 transition-opacity duration-500 blur-xl`}></div>
                  
                  {/* Content */}
                  <div className="relative z-10">
                    {/* Icon */}
                    <div className="flex items-center gap-6 mb-6">
                      <div className={`w-20 h-20 bg-gradient-to-r ${step.gradient} rounded-2xl flex items-center justify-center shadow-lg`}>
                        <step.icon className="w-10 h-10 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl md:text-2xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors duration-300">
                          {step.title}
                        </h3>
                      </div>
                    </div>
                    
                    {/* Description */}
                    <p className="text-gray-300 text-base leading-relaxed group-hover:text-white transition-colors duration-300">
                      {step.description}
                    </p>

                    {/* Progress Bar */}
                    <div className="mt-6 flex items-center gap-2">
                      {journeySteps.map((_, stepIndex) =>
                    <div
                      key={stepIndex}
                      className={`h-2 rounded-full transition-all duration-500 ${
                      stepIndex <= index ?
                      `bg-gradient-to-r ${step.gradient} w-8` :
                      'bg-gray-700 w-4'}`
                      } />

                    )}
                    </div>
                  </div>

                  {/* Arrow Connector for larger screens */}
                  {index % 2 === 0 && index < journeySteps.length - 1 &&
                <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2 z-20">
                      <div className="w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                        <ChevronDown className="w-5 h-5 text-black transform rotate-[-90deg]" />
                      </div>
                    </div>
                }
                </div>
              </motion.div>
            )}
          </div>

          {/* Progress Tracker */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
            className="text-center mb-12">
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8">
              <div className="flex items-center justify-center gap-4 mb-4">
                <Trophy className="w-8 h-8 text-yellow-400" />
                <h4 className="text-xl md:text-2xl font-bold text-white">Transformação Garantida</h4>
              </div>
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-3xl font-bold text-yellow-400 mb-2">+400</div>
                  <div className="text-gray-300">Horas de Conteúdo</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-green-400 mb-2">24/7</div>
                  <div className="text-gray-300">Suporte da Lucy IA</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-blue-400 mb-2">+5000</div>
                  <div className="text-gray-300">Alunos Transformados</div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Final CTA */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 1 }}
            className="text-center">
            

















          </motion.div>
        </div>
      </motion.section>

      {/* Final CTA */}
      <motion.section
        id="oferta-final"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="pt-[32px] pl-[16px] pr-[16px] pb-[32px]">
        <div className="max-w-2xl mx-auto text-center">
          <div className="relative bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl p-8 text-black">
            {/* Selo de 14 dias de garantia */}
            <div className="absolute -top-4 -right-4 z-10">
              <div className="bg-green-500 text-white rounded-full w-24 h-24 flex flex-col items-center justify-center text-xs font-bold shadow-lg border-4 border-white transform rotate-12">
                <Shield className="w-4 h-4 mb-1" />
                <span>14 DIAS</span>
                <span>GARANTIA</span>
              </div>
            </div>

            {/* Logo de parceria ValeBonus na oferta final */}
            <div className="absolute top-4 left-4 flex flex-col items-center gap-1 text-black/70">
              <span className="text-xs font-bold">Parceria</span>
              <img
                src="https://cdn-static-lumi.artvibe.ai/f9/f9287e35f7f4c01673c767c3df089f15.png"
                alt="ValeBonus"
                className="h-12 w-auto opacity-80" />
            </div>

            <Zap className="w-12 h-12 mx-auto mb-4" />
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              ACESSO VITALÍCIO
            </h3>
            
            {/* Preço riscado */}
            <div className="mb-4">
              <div className="text-lg line-through text-gray-700 mb-2">
                De R$ 397,00
              </div>
              <div className="text-lg font-semibold mb-2">
                Por apenas
              </div>
            </div>
            
            {/* Preços principais */}
            <div className="space-y-4 mb-6">
              <div className="text-3xl md:text-4xl font-bold">R$ 285,00 à vista <span className="text-lg">(no cartão ou pix)</span>

              </div>
              <div className="text-lg">
                ou <span className="text-2xl font-bold">10x R$ 32,09</span> no cartão de crédito
              </div>
            </div>
            
            <a
              href="https://checkout.ewpass.com.br/subscribe/ewpass-pro-blackfriday-valebonus?&coupon=ewbfvalebonus"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white font-bold text-xl px-12 py-4 rounded-full hover:bg-gray-900 transition-all duration-300 transform hover:scale-105 w-full inline-block">
              GARANTIR ACESSO VITALÍCIO AGORA
            </a>
            
            <div className="flex items-center justify-center gap-2 mt-4 text-sm">
              <img
                src="https://static.lumi.new/a7/a7bda956ae1a1aa02b529366031aaf05.webp"
                alt="Compra Segura"
                className="h-11 w-auto rounded-lg" />

            </div>
            
            <p className="text-gray-700 text-sm mt-2">* Oferta válida apenas hoje!

            </p>
          </div>
          
          {/* Nova seção: Disponível para estudar offline - aproximada da oferta */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-6">
            <h4 className="text-lg md:text-xl font-bold text-white mb-4">Também disponível em dispositivos móveis</h4>
            <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-4">
              <a
                href="https://play.google.com/store/apps/details?id=com.ewpass.mobile&hl=pt_BR"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105">
                <img
                  src="https://static.lumi.new/04/04a167a550be2df080da221af752c2d8.webp"
                  alt="Disponível no Google Play"
                  className="h-12 w-auto" />
              </a>
              <a
                href="https://apps.apple.com/br/app/ew-pass/id6752664261"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-transform hover:scale-105">
                <img
                  src="https://static.lumi.new/41/4128788013032cb2940c52b89df93f82.png"
                  alt="Disponível na App Store"
                  className="h-12 w-auto"></img>
              </a>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Seção de Garantia Incondicional - Separada e com largura da Journey */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.4 }}
        className="pt-[32px] pl-[16px] pr-[16px] pb-[32px]">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-3xl p-8 border border-gray-700">
            {/* Título Principal */}
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Garantia <span className="text-green-400">Incondicional</span> de 14 Dias
              </h2>
              <p className="text-lg md:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                Estamos tão confiantes no nosso método que oferecemos garantia total
              </p>
            </div>

            {/* Cards de Garantia - Layout Horizontal em 3 colunas */}
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {guaranteeFeatures.map((feature, index) => {
                const colorClasses = {
                  blue: 'from-blue-500 to-blue-600',
                  green: 'from-green-500 to-green-600',
                  purple: 'from-purple-500 to-purple-600'
                };

                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="group bg-gray-800/50 rounded-2xl p-8 hover:bg-gray-800/80 transition-all duration-300 border border-gray-700 hover:border-gray-600 text-center">
                    
                    <div className={`w-20 h-20 bg-gradient-to-r ${colorClasses[feature.color]} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg`}>
                      <feature.icon className="w-10 h-10 text-white" />
                    </div>
                    
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4 group-hover:text-yellow-400 transition-colors duration-300">
                      {feature.title}
                    </h3>
                    
                    <p className="text-base text-gray-300 leading-relaxed group-hover:text-white transition-colors duration-300">
                      {feature.description}
                    </p>
                  </motion.div>);

              })}
            </div>

            {/* Como Funciona Nossa Garantia - Layout Horizontal */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="text-center">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-8">
                Como Funciona Nossa Garantia?
              </h3>
              
              <div className="flex flex-col lg:flex-row justify-center items-center gap-8 max-w-5xl mx-auto">
                {guaranteeSteps.map((step, index) =>
                <div key={index} className="flex flex-col lg:flex-row items-center gap-6 text-center lg:text-left">
                    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center text-white text-xl font-bold shadow-lg flex-shrink-0">
                      {step.number}
                    </div>
                    
                    <div>
                      <h4 className="text-lg font-bold text-white mb-2">
                        {step.title}
                      </h4>
                      <p className="text-gray-300 text-base leading-relaxed max-w-xs">
                        {step.description}
                      </p>
                    </div>
                    
                    {index < guaranteeSteps.length - 1 &&
                  <ChevronDown className="hidden lg:block w-6 h-6 text-gray-500 rotate-[-90deg] mx-4" />
                  }
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Problem Section */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="bg-gray-900/30 pt-[32px] pl-[16px] pr-[16px] pb-[32px]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Por que a maioria <span className="text-red-500">desiste</span> de aprender inglês?
            </h2>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto">
              Os métodos tradicionais não funcionam para a vida real. Veja os principais obstáculos:
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {problems.map((problem, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-300">
                
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <problem.icon className="w-8 h-8 text-red-500" />
                </div>
                
                <h3 className="text-lg font-bold text-gray-900 mb-4">
                  {problem.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed text-sm">
                  {problem.description}
                </p>
              </motion.div>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="text-center">
            <div className="bg-gradient-to-r from-red-600/20 to-red-800/20 rounded-2xl p-8 border border-red-500/30">
              <p className="text-xl md:text-2xl font-bold text-white">
                <span className="text-red-500">Resultado:</span> Frustração, dinheiro perdido e o sonho da fluência cada vez mais distante.
              </p>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Disclaimer Section - Nova seção reorganizada */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="py-16 px-4 bg-black">
        <div className="max-w-6xl mx-auto">
          {/* Título Principal */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-yellow-400 mb-8 leading-tight">
              Uma em cada 5 pessoas fala inglês no mundo
            </h2>
            <div className="space-y-6 text-lg md:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              <p>
                Todo dia novas oportunidades surgem para quem domina ou no mínimo entende bem o inglês e o EW Pass é a chave, o passaporte definitivo para estudar inglês e estar pronto para novas oportunidades na vida profissional e pessoal.
              </p>
              <p className="text-yellow-400 font-semibold">
                Não seja como 80% das pessoas, aprenda inglês e aproveite novas oportunidades na sua vida.
              </p>
            </div>
          </motion.div>

          {/* Grid de Benefícios do Inglês */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {englishBenefits.map((benefit, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-3xl overflow-hidden hover:border-yellow-400/50 transition-all duration-300 hover:transform hover:scale-105">
                
                {/* Imagem */}
                <div className="aspect-video overflow-hidden">
                  <img
                  src={benefit.image}
                  alt={benefit.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                </div>
                
                {/* Conteúdo */}
                <div className="p-6">
                  {/* Ícone e Título */}
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg">
                      <benefit.icon className="w-6 h-6 text-black" />
                    </div>
                    <h3 className="text-lg font-bold text-white group-hover:text-yellow-400 transition-colors duration-300">
                      {benefit.title}
                    </h3>
                  </div>
                  
                  {/* Descrição */}
                  <p className="text-gray-300 text-sm leading-relaxed group-hover:text-white transition-colors duration-300">
                    {benefit.description}
                  </p>
                </div>
              </motion.div>
            )}
          </div>

          {/* Linha divisória decorativa */}
          <motion.div
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="w-full h-1 bg-gradient-to-r from-transparent via-yellow-400 to-transparent rounded-full mt-16">
          </motion.div>
        </div>
      </motion.section>

      {/* Solution Section - Nova Seção */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="py-16 px-4 bg-gradient-to-br from-gray-900 via-black to-gray-900">
        <div className="max-w-6xl mx-auto">
          {/* Título Principal */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              A solução que você estava <span className="text-green-400">procurando</span>
            </h2>
            <p className="text-lg md:text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Só com o EW Pass você tem acesso ao <span className="text-green-400 font-semibold">método GO 4D</span>, uma abordagem revolucionária que torna o 
              aprendizado de inglês mais rápido, eficiente e definitivo.
            </p>
          </motion.div>

          {/* Grid de Features */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {solutionFeatures.map((feature, index) =>
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 text-center hover:border-green-400/50 transition-all duration-300 hover:transform hover:scale-105">
                
                {/* Ícone */}
                <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:shadow-green-400/20">
                  <feature.icon className="w-10 h-10 text-white" />
                </div>
                
                {/* Título */}
                <h3 className="text-xl md:text-2xl font-bold text-white mb-4 group-hover:text-green-400 transition-colors duration-300">
                  {feature.title}
                </h3>
                
                {/* Descrição */}
                <p className="text-gray-300 text-sm leading-relaxed group-hover:text-white transition-colors duration-300">
                  {feature.description}
                </p>
              </motion.div>
            )}
          </div>

          {/* Botão CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="text-center">
            <button
              onClick={scrollToOffer}
              className="bg-gradient-to-r from-green-500 to-green-600 text-white font-bold text-xl px-12 py-4 rounded-full hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-green-400/30">
              Garantir meu acesso vitalício agora
            </button>
          </motion.div>
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Perguntas <span className="text-yellow-400">Frequentes</span>
          </h2>
          
          <div className="space-y-4">
            {faqData.map((faq, index) =>
            <div
              key={index}
              className="bg-gray-900 border border-gray-700 rounded-xl overflow-hidden hover:border-yellow-400 transition-all duration-300">
                
                <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-inset">
                  <span className="text-lg font-semibold text-white pr-4">
                    {faq.question}
                  </span>
                  {openFAQ === index ?
                <ChevronUp className="w-6 h-6 text-yellow-400 flex-shrink-0" /> :

                <ChevronDown className="w-6 h-6 text-yellow-400 flex-shrink-0" />
                }
                </button>
                
                <motion.div
                initial={false}
                animate={{
                  height: openFAQ === index ? 'auto' : 0,
                  opacity: openFAQ === index ? 1 : 0
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden">
                  <div className="px-6 pb-4">
                    <div className="border-t border-gray-700 pt-4">
                      <p className="text-gray-300 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </div>
        </div>
      </motion.section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-800">
        <div className="max-w-6xl mx-auto">
          {/* Nova Seção: Por que escolher o EW Pass, da English Work */}
          <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                Por que escolher o EW Pass, da <span className="text-yellow-400">English Work?</span>
              </h2>
            </div>

            <div className="space-y-16">
              {/* Primeiro bloco: Metodologia Internacional - Texto à esquerda, imagem à direita */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Texto */}
                <div className="space-y-6">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                    Metodologia Internacional
                  </h3>
                  
                  <div className="space-y-4 text-gray-300 leading-relaxed text-base">
                    <p>
                      Nossa metodologia de ensino foi desenvolvida de acordo com o <span className="text-yellow-400 font-semibold">CEFR - Common European Framework of Reference for Languages</span>, em português, Quadro Europeu Comum de Referência para Línguas, um padrão desenvolvido para descrever os níveis de proficiência linguística.
                    </p>
                    <p>
                      Ele fornece uma estrutura para a aprendizagem, o ensino e a avaliação de línguas em toda a Europa e é cada vez mais utilizado globalmente.
                    </p>
                  </div>
                </div>

                {/* CEFR Framework Visual */}
                <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 hover:border-yellow-400/50 transition-all duration-300">
                  <div className="text-center mb-6">
                    <h4 className="text-lg font-semibold text-yellow-400 mb-4">
                      CEFR: Common European Framework of Reference for Languages
                    </h4>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-center mb-6">
                    <div className="bg-green-600 rounded-lg p-4">
                      <div className="text-white font-bold text-sm mb-3">BEGINNER</div>
                      <div className="space-y-2">
                        <div className="bg-orange-400 rounded p-2 text-black font-bold text-xs">A1</div>
                        <div className="bg-blue-500 rounded p-2 text-white font-bold text-xs">A2</div>
                      </div>
                    </div>
                    
                    <div className="bg-blue-700 rounded-lg p-4">
                      <div className="text-white font-bold text-sm mb-3">INDEPENDENT</div>
                      <div className="space-y-2">
                        <div className="bg-blue-400 rounded p-2 text-white font-bold text-xs">B1</div>
                        <div className="bg-blue-300 rounded p-2 text-black font-bold text-xs">B2</div>
                      </div>
                    </div>
                    
                    <div className="bg-blue-900 rounded-lg p-4">
                      <div className="text-white font-bold text-sm mb-3">PROFICIENT</div>
                      <div className="space-y-2">
                        <div className="bg-yellow-400 rounded p-2 text-black font-bold text-xs">C1</div>
                        <div className="bg-blue-900 rounded p-2 text-white font-bold text-xs">C2</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <div className="text-xs text-gray-400">
                      Breakthrough • Waystage • Threshold • Vantage • Advanced • Mastery
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Segundo bloco: Democratizando o Ensino - Imagem à esquerda, texto à direita */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4 }}
                className="grid lg:grid-cols-2 gap-12 items-center">
                
                {/* Imagem/Visual */}
                <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-700 rounded-2xl p-8 hover:border-yellow-400/50 transition-all duration-300 text-center">
                  <div className="text-center mb-6">
                    <img
                      src="https://static.lumi.new/c0/c0161516f46a51231baabd52ffafbe61.png"
                      alt="English Work Logo"
                      className="w-full max-w-xs mx-auto h-auto rounded-lg" />

                  </div>
                  
                  <div className="space-y-6">
                    <div className="text-6xl md:text-8xl font-bold text-yellow-400">+40

                    </div>
                    <div className="text-xl md:text-2xl font-semibold text-white">
                      Anos de Experiência
                    </div>
                    <div className="text-gray-300 leading-relaxed">
                      Ensinando inglês para crianças e adultos com metodologia própria
                    </div>
                  </div>
                </div>

                {/* Texto */}
                <div className="space-y-6">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-6">
                    Estamos democratizando o ensino de inglês no Brasil
                  </h3>
                  
                  <div className="space-y-6 text-gray-300 leading-relaxed text-base">
                    <p>
                      São mais de 40 anos ensinando inglês para crianças e adultos. Experiência que usamos para desenvolver nossa metodologia própria – o <span className="text-yellow-400 font-semibold">Método Go</span> – focando nas 4 habilidades – <span className="text-yellow-400 font-semibold">ouvir, falar, ler e escrever</span>.
                    </p>
                    
                    <p>
                      Com técnicas de absorção e retenção do conteúdo, o aluno é preparado para dominar simultaneamente pronúncia, significado e regras gramaticais enquanto se expressa.
                    </p>
                    
                    <p>
                      Com preço acessível para todos, estamos democratizando o ensino de inglês e ajudando cada vez mais a aumentar o baixo número de brasileiros que domina e fala inglês com fluência!
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.section>

          {/* Novo botão CTA acima de Selos e Certificados */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12">
            <button
              onClick={scrollToOffer}
              className="bg-gradient-to-r from-green-500 to-green-600 text-white font-bold text-lg px-8 py-4 rounded-full hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-green-400/30 max-w-4xl mx-auto">Nunca mais gaste um centavo em cursos de inglês na sua vida. Garanta seu acesso vitalício agora!

            </button>
          </motion.div>

          {/* Seção de Selos e Formas de Pagamento em duas colunas */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Coluna 1: Selos e certificados */}
              <div className="text-center">
                <h4 className="text-lg font-semibold text-white mb-4">Selos e certificados</h4>
                <div className="rounded-xl pt-[9px] pl-[6px] pr-[6px] pb-[9px] bg-[# #111827] bg-[# #111827] bg-[#111827]">
                  <img
                    src="https://static.lumi.new/7c/7c36b926391861d4d0482b0e115e6fef.png"
                    alt="Selos e certificados - Navegação Segura Google, Visa, Mastercard, Hipercard, American Express, Elo, Diners Club, Boleto, Pix"
                    className="w-full h-auto rounded-lg object-contain bg-[#00000000]"></img>

                </div>
              </div>
              
              {/* Coluna 2: Formas de Pagamento */}
              <div className="text-center">
                <h4 className="text-lg font-semibold text-white mb-4">Formas de pagamento</h4>
                <div className="bg-gray-900 rounded-xl pt-[15px] pl-[6px] pr-[6px] pb-[15px]">
                  <img
                    src="https://static.lumi.new/de/dec8d8bd1438cce9d9df26001d1b68d4.png"
                    alt="Formas de pagamento - Distribuidora exclusiva do TOEFL ITP no Brasil, Mastertest Educational, Verificada por ReclameAQUI"
                    className="w-full h-auto rounded-lg"></img>

                </div>
              </div>
            </div>
          </motion.div>

          {/* Seção de Redes Sociais */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="mb-8">
            <div className="text-center">
              <h4 className="text-lg font-semibold text-white mb-6">Siga-nos nas redes sociais</h4>
              <div className="flex justify-center items-center gap-6">
                <a
                  href="https://www.facebook.com/englishwork.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-900 hover:bg-blue-600 p-3 rounded-full transition-all duration-300 transform hover:scale-110">
                  <Facebook className="w-6 h-6 text-white" />
                </a>
                <a
                  href="https://www.instagram.com/ewpass/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-900 hover:bg-pink-600 p-3 rounded-full transition-all duration-300 transform hover:scale-110">
                  <Instagram className="w-6 h-6 text-white" />
                </a>
                <a
                  href="https://www.youtube.com/@englishwork"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-900 hover:bg-red-600 p-3 rounded-full transition-all duration-300 transform hover:scale-110">
                  <Youtube className="w-6 h-6 text-white" />
                </a>
                <a
                  href="https://www.linkedin.com/in/english-work/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-900 hover:bg-blue-700 p-3 rounded-full transition-all duration-300 transform hover:scale-110">
                  <Linkedin className="w-6 h-6 text-white" />
                </a>
                <a
                  href="https://www.tiktok.com/@englishwork_br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gray-900 hover:bg-black p-3 rounded-full transition-all duration-300 transform hover:scale-110">
                  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-.88-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Texto de Copyright */}
          <div className="text-center text-gray-500">
            <p>&copy; EW Pass - Seu passaporte definitivo para aprender inglês. </p>
            <p>EW Pass é um produto da English Work Sociedade de Ensino Ltda</p>
            <p> © 2025 - Todos os direitos reservados | CNPJ: 48.620.557/0001-51</p>
          </div>
        </div>
      </footer>

      {/* Botão Flutuante do WhatsApp */}
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1, duration: 0.5 }}
        className="fixed bottom-6 right-6 z-50">
        <a
          href="https://wa.me/5527997139118?text=Olá! Gostaria de saber mais sobre a Black Friday da EW Pass."
          target="_blank"
          rel="noopener noreferrer"
          className="bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 flex items-center gap-3 group">
          <MessageCircle className="w-6 h-6" />
          <span className="hidden group-hover:block whitespace-nowrap bg-gray-900 text-white px-3 py-1 rounded-lg absolute right-full mr-3 text-sm">
            Fale conosco!
          </span>
        </a>
      </motion.div>
    </div>);

};

export default ValeBonus;